/**
 * MasterListsSection — master lists and the merge tool (plan §11 tab 6:
 * "Admin: master lists and merge tool ..."; §13.4 WP-33b frontend scope: "a
 * list-type selector over the nine §5.2 types, a search box, the entries
 * table with aliases, section and CPT, a create-entry form, an add-alias
 * control, a match dialog ..., a merge control ..."). Mounted by AdminTab.
 */
import { useEffect, useMemo, useState } from 'react';
import { Plus, ListChecks, SearchCode } from 'lucide-react';
import {
  PageHeader,
  SearchInput,
  FilterSelect,
  EmptyState,
  ToastProvider,
  primaryBtnCls,
  secondaryBtnCls,
  outlineBtnCls,
} from '../../../../ui';
import { useContracts } from '../../../ContractsProvider';
import { listMasterEntries } from '../../../api/client';
import type { MasterEntry, MasterEntryRow, MasterListType, MasterMergeResult } from '../../../api/client';
import { MASTER_LIST_TYPE_OPTIONS, MASTER_LIST_TYPE_LABEL } from './listTypes';
import { CreateEntryModal } from './CreateEntryModal';
import { AddAliasModal } from './AddAliasModal';
import { MatchDialog } from './MatchDialog';
import { MergeModal } from './MergeModal';

function MasterListsSectionInner() {
  const { networkId } = useContracts();
  const [listType, setListType] = useState<MasterListType>('procedure');
  const [entries, setEntries] = useState<MasterEntry[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [showMatch, setShowMatch] = useState(false);
  const [aliasTarget, setAliasTarget] = useState<MasterEntry | null>(null);
  const [mergeTarget, setMergeTarget] = useState<MasterEntry | null>(null);

  useEffect(() => {
    if (!networkId) return;
    let cancelled = false;
    setEntries(null);
    setLoadError(null);
    listMasterEntries(listType)
      .then((rows) => {
        if (!cancelled) setEntries(rows);
      })
      .catch(() => {
        if (!cancelled) setLoadError('Could not load the master list.');
      });
    return () => {
      cancelled = true;
    };
  }, [networkId, listType]);

  const filtered = useMemo(() => {
    if (!entries) return [];
    const term = search.trim().toLowerCase();
    if (!term) return entries;
    return entries.filter((entry) => {
      if (entry.official_name.toLowerCase().includes(term)) return true;
      if (entry.code && entry.code.toLowerCase().includes(term)) return true;
      return entry.aliases.some((alias) => alias.alias.toLowerCase().includes(term));
    });
  }, [entries, search]);

  /** POST /master/:type's response carries no `aliases` — a fresh entry has none. */
  function handleCreated(entry: MasterEntryRow) {
    setEntries((prev) => [...(prev ?? []), { ...entry, aliases: [] }]);
  }

  function handleAliasAdded(updated: MasterEntry) {
    setEntries((prev) => (prev ?? []).map((entry) => (entry.id === updated.id ? updated : entry)));
    setAliasTarget(null);
  }

  /**
   * A decide() outcome (matched/new_entry/rejected). `matched` may also have
   * just created an alias on an entry we already hold, and `new_entry` adds
   * one we don't — reloading this list_type is simpler and more correct than
   * hand-patching local state around a response shape with no `aliases` field.
   */
  function handleDecided(_entry: MasterEntryRow | null) {
    listMasterEntries(listType)
      .then(setEntries)
      .catch(() => {});
  }

  function handleMerged(_result: MasterMergeResult) {
    // The survivor's aliases changed and the loser dropped out of the live
    // list — simplest correct thing is to reload this list_type.
    listMasterEntries(listType)
      .then(setEntries)
      .catch(() => {});
  }

  if (!networkId) {
    return <EmptyState icon={ListChecks} title="No site selected" hint="Choose a site to see its master lists." />;
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Master lists"
        icon={ListChecks}
        count={entries?.length ?? undefined}
        subtitle="One controlled list per tag type (§5.2): sponsors, CROs, procedures, fees and more, with every alias they have been called."
        actions={
          <>
            <button
              type="button"
              onClick={() => setShowMatch(true)}
              className={outlineBtnCls()}
              data-testid="open-match-dialog"
            >
              <SearchCode className="h-4 w-4" />
              Match text
            </button>
            <button type="button" onClick={() => setShowCreate(true)} className={primaryBtnCls} data-testid="open-create-entry">
              <Plus className="h-4 w-4" />
              New entry
            </button>
          </>
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        <FilterSelect
          label="List type"
          value={listType}
          onChange={(v) => setListType(v as MasterListType)}
          options={MASTER_LIST_TYPE_OPTIONS}
          auto
        />
        <SearchInput
          value={search}
          onChange={setSearch}
          label="Search master list entries"
          placeholder="Search name, alias or code…"
          className="flex-1 basis-64"
        />
      </div>

      {loadError && <p className="text-sm text-red-600 dark:text-red-400">{loadError}</p>}
      {entries === null && !loadError && <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>}

      {entries !== null && filtered.length === 0 && (
        <EmptyState
          icon={ListChecks}
          title={entries.length === 0 ? `No ${MASTER_LIST_TYPE_LABEL[listType].toLowerCase()} entries yet` : 'No entries match'}
          hint={entries.length === 0 ? 'Create the first entry, or match free text against this list.' : 'Try a different search.'}
          variant="card"
        />
      )}

      {filtered.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
          <table className="w-full divide-y divide-gray-200 text-sm dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800/50">
              <tr>
                {['Name', 'Section', 'Code', 'Aliases', ''].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {filtered.map((entry) => (
                <tr key={entry.id} data-testid={`master-entry-row-${entry.id}`}>
                  <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100">{entry.official_name}</td>
                  <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{entry.section ?? '—'}</td>
                  <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                    {entry.code ? `${entry.code_type ?? ''} ${entry.code}`.trim() : '—'}
                  </td>
                  <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                    {entry.aliases.length > 0 ? entry.aliases.map((alias) => alias.alias).join(', ') : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => setAliasTarget(entry)}
                        className={outlineBtnCls()}
                        data-testid={`add-alias-${entry.id}`}
                      >
                        Add alias
                      </button>
                      <button
                        type="button"
                        onClick={() => setMergeTarget(entry)}
                        disabled={(entries?.length ?? 0) < 2}
                        className={secondaryBtnCls}
                        data-testid={`merge-entry-${entry.id}`}
                      >
                        Merge…
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CreateEntryModal isOpen={showCreate} listType={listType} onClose={() => setShowCreate(false)} onCreated={handleCreated} />
      <MatchDialog isOpen={showMatch} listType={listType} onClose={() => setShowMatch(false)} onDecided={handleDecided} />
      <AddAliasModal
        isOpen={aliasTarget !== null}
        listType={listType}
        entry={aliasTarget}
        onClose={() => setAliasTarget(null)}
        onAdded={handleAliasAdded}
      />
      <MergeModal
        isOpen={mergeTarget !== null}
        listType={listType}
        loser={mergeTarget}
        candidates={(entries ?? []).filter((entry) => entry.id !== mergeTarget?.id)}
        onClose={() => setMergeTarget(null)}
        onMerged={handleMerged}
      />
    </div>
  );
}

export function MasterListsSection() {
  return (
    <ToastProvider>
      <MasterListsSectionInner />
    </ToastProvider>
  );
}

export default MasterListsSection;
